import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const ResultadosFormulario = () => {
  const { formularioId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [formulario, setFormulario] = useState(null);
  const [respuesta, setRespuesta] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResultados();
  }, [formularioId]);

  const loadResultados = async () => {
    try {
      // Students use mi-respuesta endpoint, not the general respuestas endpoint
      const [formularioRes, miRespuestaRes, preguntasRes] = await Promise.all([
        axios.get(`${API}/formularios/${formularioId}`),
        axios.get(`${API}/formularios/${formularioId}/mi-respuesta`),
        axios.get(`${API}/formularios/${formularioId}/preguntas`)
      ]);
      
      setFormulario(formularioRes.data);
      setPreguntas(preguntasRes.data);
      
      // Use student's own response
      const miRespuesta = miRespuestaRes.data;
      
      if (!miRespuesta.calificado) {
        toast.info('Tu respuesta aún no ha sido calificada por el profesor');
        navigate('/app/clases');
        return;
      }
      
      setRespuesta(miRespuesta);
      
    } catch (error) {
      console.error('Error loading resultados:', error);
      
      if (error.response?.status === 404) {
        toast.error('No se encontró tu respuesta para este formulario');
      } else if (error.response?.status === 403) {
        toast.error('No tienes permisos para ver estos resultados');
      } else {
        toast.error('Error al cargar los resultados. Intenta nuevamente.');
      }
      
      navigate('/app/clases');
    } finally {
      setLoading(false);
    }
  };

  const getPreguntaById = (preguntaId) => {
    return preguntas.find(p => p.id === preguntaId);
  };

  const getRespuestaByPreguntaId = (preguntaId) => {
    return respuesta?.respuestas?.find(r => r.pregunta_id === preguntaId) || null;
  };

  const getNotaColor = (puntuacion, maximo) => {
    const porcentaje = (puntuacion / maximo) * 100;
    if (porcentaje >= 90) return 'text-green-600 bg-green-50';
    if (porcentaje >= 70) return 'text-blue-600 bg-blue-50';
    if (porcentaje >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getNotaLetra = (puntuacion, maximo) => {
    const porcentaje = (puntuacion / maximo) * 100;
    if (porcentaje >= 90) return 'A';
    if (porcentaje >= 80) return 'B';
    if (porcentaje >= 70) return 'C';
    if (porcentaje >= 60) return 'D';
    return 'F';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  if (!formulario || !respuesta) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Resultados no disponibles</p>
        <Button 
          onClick={() => navigate('/app/clases')} 
          className="mt-4"
        >
          Volver a Clases
        </Button>
      </div>
    );
  }

  const puntuacionTotal = respuesta?.puntuacion_total || 0;
  const puntuacionMaxima = preguntas?.reduce((total, pregunta) => total + (pregunta.puntuacion || 0), 0) || 0;
  const porcentaje = puntuacionMaxima > 0 ? (puntuacionTotal / puntuacionMaxima) * 100 : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/app/clases')}
          className="mb-4 text-blue-600 hover:text-blue-800"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver a Clases
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Resultados: {formulario?.titulo || 'Formulario'}
            </h1>
            <p className="text-gray-600 mb-4">
              {formulario?.descripcion || ''}
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <i className="fas fa-user mr-1"></i>
                Profesor: {formulario?.profesor_nombre || 'No especificado'}
              </div>
              <div className="flex items-center">
                <i className="fas fa-calendar mr-1"></i>
                Respondido: {respuesta?.fecha_envio ? new Date(respuesta.fecha_envio).toLocaleDateString() : 'No disponible'}
              </div>
              {respuesta?.fecha_calificacion && (
                <div className="flex items-center">
                  <i className="fas fa-check-circle mr-1"></i>
                  Calificado: {new Date(respuesta.fecha_calificacion).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
          <Badge className="bg-green-100 text-green-800">
            <i className="fas fa-star mr-1"></i>
            Calificado
          </Badge>
        </div>
      </div>

      {/* Overall Score */}
      <Card className={`${getNotaColor(puntuacionTotal, puntuacionMaxima)} border-2`}>
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="text-6xl font-bold">
                {getNotaLetra(puntuacionTotal, puntuacionMaxima)}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">
                  {puntuacionTotal.toFixed(1)} / {puntuacionMaxima}
                </div>
                <div className="text-lg">
                  {porcentaje.toFixed(1)}%
                </div>
              </div>
            </div>
            <div className="text-lg font-semibold">
              {porcentaje >= 90 && '¡Excelente trabajo! 🎉'}
              {porcentaje >= 70 && porcentaje < 90 && '¡Buen trabajo! 👍'}
              {porcentaje >= 60 && porcentaje < 70 && 'Puedes mejorar 💪'}
              {porcentaje < 60 && 'Necesita refuerzo 📚'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions and Results */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <i className="fas fa-clipboard-check mr-2 text-blue-600"></i>
          Detalles de la Calificación
        </h2>
        
        {preguntas && preguntas.length > 0 ? preguntas.map((pregunta, index) => {
          const respuestaPregunta = getRespuestaByPreguntaId(pregunta.id);
          const puntuacionObtenida = respuestaPregunta?.puntuacion || 0;
          const comentario = respuestaPregunta?.comentario || '';
          
          return (
            <Card key={pregunta.id} className="shadow-sm">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg flex items-start">
                    <span className="text-blue-600 mr-2 shrink-0">{index + 1}.</span>
                    <div 
                      className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1 prose-ul:my-1 prose-ol:my-1"
                      dangerouslySetInnerHTML={{ __html: pregunta.titulo }}
                    />
                  </CardTitle>
                  <div className="flex items-center space-x-2 ml-4 shrink-0">
                    <Badge variant="outline" className="text-xs">
                      {pregunta.puntuacion} pts máximo
                    </Badge>
                    <Badge 
                      className={`${getNotaColor(puntuacionObtenida, pregunta.puntuacion)} text-xs font-medium`}
                    >
                      {puntuacionObtenida} pts
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Student Answer */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <i className="fas fa-user text-blue-600 mr-2"></i>
                    <span className="font-medium text-blue-800">Tu respuesta:</span>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    {respuestaPregunta?.respuesta || 'Sin respuesta'}
                  </div>
                </div>

                {/* Correct Answer (if exists) */}
                {pregunta.respuesta_correcta && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-check text-green-600 mr-2"></i>
                      <span className="font-medium text-green-800">Respuesta correcta:</span>
                    </div>
                    <div className="bg-white p-3 rounded border">
                      {pregunta.respuesta_correcta}
                    </div>
                  </div>
                )}

                {/* Professor Comment */}
                {comentario && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-comment text-yellow-600 mr-2"></i>
                      <span className="font-medium text-yellow-800">Comentario del profesor:</span>
                    </div>
                    <div className="bg-white p-3 rounded border italic">
                      "{comentario}"
                    </div>
                  </div>
                )}

                {/* Score Bar */}
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">Puntuación</span>
                    <span className="text-sm font-bold">
                      {puntuacionObtenida} / {pregunta.puntuacion}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((puntuacionObtenida / pregunta.puntuacion) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }) : (
          <Card className="shadow-sm">
            <CardContent className="text-center py-8">
              <i className="fas fa-question-circle text-gray-300 text-4xl mb-3"></i>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay preguntas disponibles</h3>
              <p className="text-gray-500">No se encontraron preguntas para este formulario.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary and Actions */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Resumen de Resultados
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {preguntas?.length || 0}
                </div>
                <div className="text-sm text-gray-600">Preguntas</div>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {preguntas?.filter(p => {
                    const resp = getRespuestaByPreguntaId(p.id);
                    return (resp?.puntuacion || 0) === (p.puntuacion || 0);
                  }).length || 0}
                </div>
                <div className="text-sm text-gray-600">Correctas</div>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {porcentaje.toFixed(0)}%
                </div>
                <div className="text-sm text-gray-600">Calificación</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultadosFormulario;